import { useCallback, useMemo } from 'react';
import { trpc } from '../../trpc/react';
import { useViewer } from '../context/viewer';
import { BenchmarkGrid } from '@strength/domain';
import { useOrgScopedMetrics } from '../pages/admin/workout-import/OrgScopedMetricsContext';

/**
 * Checks whether any metric input for the given intensity metric contains a percentage value.
 */
export function hasPercentageIntensity(
  metric: { metricType: string; metricId: string },
  metricInputs: readonly { metricId: string; input: string }[],
): boolean {
  if (metric.metricType !== 'intensity') return false;
  return metricInputs.some(
    (m) => m.metricId === metric.metricId && m.input.includes('%'),
  );
}

/**
 * Returns the benchmark grid name when the badge should be shown, or `null` otherwise.
 * Consolidates the badge-visibility logic used by metric header components.
 */
export function getBenchmarkBadgeGridName(
  metric: { metricType: string; benchmarkGridId?: string },
  getBenchmarkGrid: (id: string) => BenchmarkGrid | null,
  defaultBenchmarkGridId: string,
  opts: {
    libraryExerciseId?: string;
    hasPercentageIntensity?: boolean;
  },
): string | null {
  if (metric.metricType !== 'intensity') return null;
  if (!opts.hasPercentageIntensity || !opts.libraryExerciseId) return null;

  const grid = getBenchmarkGrid(
    metric.benchmarkGridId ?? defaultBenchmarkGridId,
  );
  if (!grid) return null;
  if (grid.isDefault) return null;

  const inGrid = grid.coreExercises.some(
    (e) => e.libraryExerciseId === opts.libraryExerciseId,
  );
  if (!inGrid) return null;

  return grid.name;
}

/**
 * Returns the rep benchmark grids on which the given exercise is a core exercise.
 *
 * Used to populate and gate the "Select Benchmark Metric" submenu: it should list
 * only grids where the exercise is a core member, and the submenu is disabled when
 * there are none (PRD req 8). KPI grids are excluded since KPI benchmarks are not
 * yet supported for this submenu.
 */
export function getRepBenchmarkGridsForExercise(
  benchmarkGrids: BenchmarkGrid[],
  libraryExerciseId: string | undefined,
): BenchmarkGrid[] {
  if (!libraryExerciseId) return [];
  return benchmarkGrids.filter(
    (grid) =>
      grid.type === 'rep' &&
      grid.coreExercises.some((e) => e.libraryExerciseId === libraryExerciseId),
  );
}

/**
 * Preloads all benchmarks for the organization and provides cached access.
 * @param teamIdOverride - Optional team ID to use instead of the viewer's selected team.
 *   Required for tablet mode where selectedTeam may not be set.
 */
export function useBenchmarks(teamIdOverride?: string) {
  const {
    team: { selectedTeam },
  } = useViewer();
  const teamId = teamIdOverride || selectedTeam;

  // Admin pages supply org-scoped benchmark grids via context (admin sessions
  // can't call the teamProcedure benchmark endpoints). When present, skip the
  // org-scoped queries; otherwise behave as before.
  const orgScoped = useOrgScopedMetrics();

  // Load all benchmarks in the background
  const query = trpc.benchmarks.fetchGrids.useQuery(undefined, {
    staleTime: Infinity,
    enabled: orgScoped === null,
  });
  // Load the default benchmark grid in the background
  const defaultQuery = trpc.benchmarks.fetchDefaultBenchmarkGrid.useQuery(
    undefined,
    {
      staleTime: Infinity,
      enabled: orgScoped === null,
    },
  );

  const { benchmarkGrids, defaultBenchmarkGrid } = useMemo(() => {
    const rawGrids = orgScoped ? orgScoped.benchmarkGrids : query.data;
    const benchmarkGrids =
      rawGrids?.map((grid) => ({
        id: grid.id,
        name: grid.name,
        type: grid.type,
        repCount: grid.repCount,
        updateType: grid.updateType,
        isDefault: grid.isDefault,
        coreExercises: grid.coreExercises,
      })) ?? [];
    const defaultBenchmarkGrid = orgScoped
      ? orgScoped.defaultBenchmarkGrid ?? null
      : defaultQuery.data;
    return { benchmarkGrids, defaultBenchmarkGrid };
  }, [orgScoped, query.data, defaultQuery.data]);

  const getBenchmarkGrid = useCallback(
    (id: string): BenchmarkGrid | null => {
      if (!benchmarkGrids.length) {
        // metrics haven't loaded yet, return null
        // this gets quickly overwritten by re-renders once the data is available
        return null;
      }
      const benchmarkGrid = benchmarkGrids.find((grid) => grid.id === id);
      if (!benchmarkGrid) {
        // Grid not found, return null instead of throwing
        // This can happen if the grid was deleted or the ID is invalid
        return null;
      }

      return benchmarkGrid;
    },
    [benchmarkGrids],
  );

  const { refetch } = query;
  const rehydrate = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    defaultBenchmarkGridId: defaultBenchmarkGrid?.id ?? '',
    benchmarkGrids,
    getBenchmarkGrid,
    isLoading: orgScoped ? orgScoped.isLoading : query.isLoading,
    error: query.error?.message,
    rehydrate,
  };
}
